import { useEffect, useRef } from 'react';

import { useReconcileCMChecklistManualOverrides } from '~/hooks/apis/offers/useCMChecklistManualOverrides';

import type { CMChecklistManualOverride } from '~/services/OffersService';

import type { GeneratedChecklistRow } from './checklistRows';
import { buildStaleManualOverrides } from './manualOverridePersistence';

interface Input {
  enabled: boolean;
  offerId: string;
  manualOverridesFromApi: Array<CMChecklistManualOverride>;
  rows: Array<GeneratedChecklistRow>;
}

export function useReconcileStaleCMChecklistManualOverrides({ enabled, offerId, manualOverridesFromApi, rows }: Input) {
  const { mutate: reconcileManualOverrides } = useReconcileCMChecklistManualOverrides(offerId);
  const reconciledSignatureRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!enabled || rows.length === 0) {
      return;
    }

    const rowsSignature = rows.map((row) => row.itemKey).join('|');
    const manualOverridesSignature = manualOverridesFromApi
      .map((manualOverride) => `${manualOverride.id}:${manualOverride.item_key}:${manualOverride.updated_at}`)
      .join('|');
    const reconcileSignature = `${offerId}:${rowsSignature}:${manualOverridesSignature}`;

    if (reconciledSignatureRef.current === reconcileSignature) {
      return;
    }

    reconciledSignatureRef.current = reconcileSignature;

    const staleManualOverrides = buildStaleManualOverrides({ manualOverridesFromApi, rows });

    if (staleManualOverrides.length === 0) {
      return;
    }

    reconcileManualOverrides(staleManualOverrides);
  }, [enabled, manualOverridesFromApi, offerId, reconcileManualOverrides, rows]);
}
